import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, consentAPI, WS_BASE_URL, API_BASE_URL } from '../services/api';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [ws, setWs] = useState(null);
  const [wsOpen, setWsOpen] = useState(false);
  const [appSettings, setAppSettings] = useState({});
  const [unreadByRoom, setUnreadByRoom] = useState(() => {
    try {
      const storedUser = localStorage.getItem('user');
      const userObj = storedUser ? JSON.parse(storedUser) : null;
      const uid = userObj?.id;
      if (!uid) return {};
      const key = `unreadByRoom_${uid}`;
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) || {}) : {};
    } catch (_) {
      return {};
    }
  });

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          // Initialize unread map for this user
          try {
            const uid = JSON.parse(storedUser || '{}')?.id;
            if (uid) {
              const key = `unreadByRoom_${uid}`;
              const raw = localStorage.getItem(key);
              setUnreadByRoom(raw ? (JSON.parse(raw) || {}) : {});
            } else {
              setUnreadByRoom({});
            }
          } catch (e) { setUnreadByRoom({}); }
          
          // Verify token and merge latest profile (completion_code, etc.)
          const meRes = await authAPI.getCurrentUser();
          if (meRes?.data) {
            localStorage.setItem('user', JSON.stringify(meRes.data));
            setUser(meRes.data);
          }
        } catch (error) {
          console.error('Token validation failed:', error);
          logout();
        }
      }
      // Load app settings for feature flags (e.g., feature.tasks)
      try {
        const res = await axios.get(`${API_BASE_URL}/settings`).catch(async () => {
          // Fallback to API_BASE_URL pattern if needed
          try {
            const r2 = await axios.get('/settings');
            return r2;
          } catch (_) { return null; }
        });
        if (res && res.data && res.data.settings) {
          setAppSettings(res.data.settings);
        }
      } catch (e) {}
      setLoading(false);
    };

    initAuth();
  }, []);

  // Global heartbeat so users stay online across all routes (e.g., questionnaire)
  useEffect(() => {
    if (!user || !token) return;
    let intervalId;
    const send = async () => {
      try {
        // Only send heartbeat if there is a global WebSocket open
        if (ws && ws.readyState === WebSocket.OPEN) {
          await authAPI.heartbeat();
        }
      } catch (e) {
        // ignore
      }
    };
    send();
    // Heartbeat every 10s
    intervalId = setInterval(send, 10000);
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [user, token]);

  // Global single WebSocket (single source of truth for presence/events)
  useEffect(() => {
    if (!user) {
      if (ws) {
        try { ws.close(); } catch (e) {}
        setWs(null);
      }
      setWsOpen(false);
      return;
    }
    if (ws) return; // already connected
    try {
      const socket = new WebSocket(`${WS_BASE_URL}/ws/${user.id}`);
      setWs(socket);
      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data?.type === 'force_logout') {
            // Admin forced a logout: clear session and return to the login screen.
            try {
              localStorage.removeItem('token');
              localStorage.removeItem('user');
            } catch (_) {}
            setToken(null);
            setUser(null);
            setWsOpen(false);
            try { socket.close(); } catch (_) {}
            try { window.location.href = `${process.env.PUBLIC_URL || ''}/login`; } catch (_) {}
            return;
          }
          if (data?.type === 'chat_ended') {
            try { localStorage.setItem('show_study_completion_code', 'true'); } catch (_) {}
          }
          // Broadcast all WS events to the app so views can react without owning a socket
          try {
            const ev = new CustomEvent('app_ws_event', { detail: data });
            window.dispatchEvent(ev);
          } catch (e) {}
          // Maintain unread counts centrally so badges persist even if sidebar isn't mounted
          try {
            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            const myId = (user && user.id) || storedUser?.id;
            if (myId && data && data.type === 'new_message') {
              // Always signal rooms_changed so both sidebars refresh
              try {
                const ridAny = data?.data?.room_id || data.room_id;
                if (ridAny) {
                  localStorage.setItem('rooms_changed', JSON.stringify({ at: Date.now(), room_id: String(ridAny) }));
                  window.dispatchEvent(new Event('storage'));
                  try {
                    const evRooms = new CustomEvent('rooms_changed', { detail: { room_id: String(ridAny) } });
                    window.dispatchEvent(evRooms);
                  } catch (e) {}
                }
              } catch (_) {}

              const roomId = data?.data?.room_id || data.room_id;
              const senderId = data?.data?.sender?.id || data?.data?.sender_id || data.sender_id || data.user_id;
              const activeRoomId = localStorage.getItem('activeRoomId');
              if (myId && roomId && senderId && String(activeRoomId) !== String(roomId) && senderId !== myId) {
                const key = `unreadByRoom_${myId}`;
                const raw = localStorage.getItem(key);
                const map = raw ? (JSON.parse(raw) || {}) : {};
                map[String(roomId)] = (map[String(roomId)] || 0) + 1;
                localStorage.setItem(key, JSON.stringify(map));
                setUnreadByRoom(map);
                try {
                  const ev3 = new CustomEvent('unread_updated', { detail: { roomId: String(roomId), total: Object.values(map).reduce((a,b)=>a+(b||0),0) } });
                  window.dispatchEvent(ev3);
                } catch (_) {}
              }
            } else if (myId && data && data.type === 'chat_ended') {
              const rid = String(data?.room_id || '');
              if (rid) {
                const key = `unreadByRoom_${myId}`;
                const raw = localStorage.getItem(key);
                const map = raw ? (JSON.parse(raw) || {}) : {};
                if (map[rid]) {
                  delete map[rid];
                  localStorage.setItem(key, JSON.stringify(map));
                  setUnreadByRoom(map);
                  try {
                    const ev4 = new CustomEvent('unread_updated', { detail: { roomId: rid, total: Object.values(map).reduce((a,b)=>a+(b||0),0) } });
                    window.dispatchEvent(ev4);
                  } catch (_) {}
                }
              }
            }
          } catch (e) {}
          if (data && data.type === 'timer_started') {
            try {
              const ridAny = data?.room_id;
              if (ridAny) {
                const rid = String(ridAny);
                if (data.timer_started_at) localStorage.setItem(`room_${rid}_timer_started_at`, data.timer_started_at);
                if (data.timer_started_at_ms) localStorage.setItem(`room_${rid}_timer_started_at_ms`, String(data.timer_started_at_ms));
                if (data.server_now_ms) localStorage.setItem(`room_${rid}_server_now_at_fetch_ms`, String(data.server_now_ms));
                localStorage.setItem(`room_${rid}_client_now_at_fetch`, String(Date.now()));
                const startMs = parseInt(String(data.timer_started_at_ms || 0), 10);
                const serverNowMs = parseInt(String(data.server_now_ms || 0), 10);
                if (startMs > 0 && serverNowMs >= startMs) {
                  // Server-time sync: avoids ~1s drift between users
                  const serverOffsetMs = serverNowMs - Date.now();
                  localStorage.setItem(`room_${rid}_server_offset_ms`, String(serverOffsetMs));
                  const elapsed = Math.max(0, serverNowMs - startMs);
                  const clientStart = Date.now() - elapsed;
                  localStorage.setItem(`room_${rid}_client_start_ms`, String(clientStart));
                }
              }
            } catch (e) {}
          }
        } catch (e) {
          // ignore parsing errors
        }
      };
      socket.onopen = () => {
        setWsOpen(true);
        try {
          const ev = new CustomEvent('app_ws_event', { detail: { type: 'ws_open' } });
          window.dispatchEvent(ev);
        } catch (e) {}
      };
      socket.onclose = () => {
        setWs(null);
        setWsOpen(false);
        try {
          const ev = new CustomEvent('app_ws_event', { detail: { type: 'ws_close' } });
          window.dispatchEvent(ev);
        } catch (e) {}
      };
      socket.onerror = () => {
        setWsOpen(false);
        try { socket.close(); } catch (e) {}
      };
    } catch (e) {
      // ignore socket errors
    }
    // Cleanup on unmount
    return () => {
      if (ws) {
        try { ws.close(); } catch (e) {}
        setWs(null);
      }
      setWsOpen(false);
    };
  }, [user, ws]);

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      // Clear any stale questionnaire redirect flags on fresh login
      try { localStorage.removeItem('pending_questionnaire'); } catch (e) {}
      // Require the consent sheet again on every login.
      try { sessionStorage.removeItem('consent_ack'); } catch (e) {}
      setToken(access_token);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      await authAPI.register(userData);
      // Do not auto-login after registration. Redirect user to Login screen.
      return { success: true };
    } catch (error) {
      console.error('Registration failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (e) {
      // ignore network/auth errors on logout
    }
    // Proactively broadcast a local event so other tabs in the same browser update immediately
    try {
      window.localStorage.setItem('presence_change', JSON.stringify({ at: Date.now(), is_online: false }));
      window.dispatchEvent(new Event('storage'));
    } catch (e) {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Drop the per-session consent acknowledgment so the next login re-shows it.
    try { sessionStorage.removeItem('consent_ack'); } catch (e) {}
    setToken(null);
    setUser(null);
    setWsOpen(false);
    setUnreadByRoom({});
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getCurrentUser();
      const userData = response.data;
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return { success: true };
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      return { success: false };
    }
  };

  const sendTypingStatus = (roomId, isTyping) => {
    try {
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      const rid = Number(roomId);
      if (!Number.isFinite(rid)) return;
      ws.send(JSON.stringify({
        type: 'typing',
        room_id: rid,
        is_typing: !!isTyping
      }));
    } catch (e) {
      // ignore socket errors
    }
  };

  const value = {
    user,
    token,
    loading,
    appSettings,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    // convenience for consent: treat falsy as not given
    hasConsent: !!user?.consent_given,
    // expose ws open state so UI can reflect connection immediately
    isWsOpen: wsOpen,
    unreadByRoom,
    joinRoom: (roomId) => {
      try {
        if (ws && ws.readyState === WebSocket.OPEN) {
          const rid = Number(roomId);
          if (!Number.isNaN(rid)) {
            ws.send(JSON.stringify({ type: 'join_room', room_id: rid }));
          }
        }
      } catch (e) {}
    },
    sendTypingStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
